import { useState } from "react";
import { Clock } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * iter40 — Tournament timezone picker.
 *
 * Only affects STORAGE of naive timestamps (from GotSport imports). The
 * Schedule and Bracket views always render in each device's local TZ via
 * `toLocaleString`. Setting this means GotSport's tz-less ISO strings
 * (e.g. "2026-06-20T08:00:00") get stored as that zone, not interpreted
 * inconsistently across browsers.
 */
const COMMON_TZS = [
    { value: "", label: "— Use UTC (default) —" },
    { value: "America/Los_Angeles", label: "Pacific (Los Angeles)" },
    { value: "America/Denver", label: "Mountain (Denver)" },
    { value: "America/Phoenix", label: "Mountain — No DST (Phoenix)" },
    { value: "America/Chicago", label: "Central (Chicago)" },
    { value: "America/New_York", label: "Eastern (New York)" },
    { value: "America/Toronto", label: "Eastern (Toronto)" },
    { value: "America/Mexico_City", label: "Central (Mexico City)" },
    { value: "America/Sao_Paulo", label: "Brasília (São Paulo)" },
    { value: "Europe/London", label: "UK (London)" },
    { value: "Europe/Paris", label: "Central Europe (Paris)" },
    { value: "Europe/Madrid", label: "Spain (Madrid)" },
    { value: "Europe/Berlin", label: "Germany (Berlin)" },
    { value: "Europe/Rome", label: "Italy (Rome)" },
    { value: "Africa/Johannesburg", label: "South Africa (Johannesburg)" },
    { value: "Asia/Dubai", label: "Gulf (Dubai)" },
    { value: "Asia/Tokyo", label: "Japan (Tokyo)" },
    { value: "Asia/Seoul", label: "Korea (Seoul)" },
    { value: "Asia/Kolkata", label: "India (Kolkata)" },
    { value: "Australia/Sydney", label: "Australia (Sydney)" },
    { value: "Pacific/Auckland", label: "New Zealand (Auckland)" },
];

export default function TournamentTimezoneCard({ tournament, isLocked, onSaved }) {
    const [tz, setTz] = useState(tournament?.tz || "");
    const [saving, setSaving] = useState(false);

    const handleSave = async () => {
        if (!tournament?.id) return;
        setSaving(true);
        try {
            await api.updateTournamentTz(tournament.id, tz || null);
            toast.success(tz ? `Timezone set to ${tz}` : "Timezone cleared (UTC)");
            onSaved?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to update timezone");
        } finally {
            setSaving(false);
        }
    };

    const dirty = (tz || "") !== (tournament?.tz || "");

    return (
        <div className="brutal-card p-5 space-y-3" data-testid="tournament-tz-card">
            <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-cyber" />
                <h2 className="font-display font-extrabold text-base uppercase">Tournament timezone</h2>
            </div>
            <p className="text-xs text-zinc-400 leading-relaxed">
                Tells the importer how to interpret kickoff times that arrive without a timezone
                (mostly GotSport). The schedule itself still shows in each crewmate's device local
                time — this just keeps storage unambiguous.
            </p>
            <div className="flex items-center gap-2 flex-wrap">
                <select
                    data-testid="tournament-tz-select"
                    value={tz}
                    onChange={(e) => setTz(e.target.value)}
                    disabled={isLocked || saving}
                    className="brutal-input flex-1 min-w-[220px]"
                >
                    {COMMON_TZS.map(o => (
                        <option key={o.value || "utc"} value={o.value}>{o.label}</option>
                    ))}
                </select>
                <button
                    type="button"
                    data-testid="tournament-tz-save-btn"
                    onClick={handleSave}
                    disabled={!dirty || isLocked || saving}
                    className="brutal-btn-primary text-xs px-4 py-2 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    {saving ? "Saving…" : "Save"}
                </button>
            </div>
        </div>
    );
}
